import React, { useState, useContext, useEffect } from 'react'
import cn from 'classnames'
import moment from 'moment'
import { makeStyles } from '@material-ui/core'
import papaParse from 'papaparse'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Link,
} from '@material-ui/core'
import FileCopyTwoToneIcon from '@material-ui/icons/FileCopyTwoTone'
import ExpandCollapseBox from 'Components/ExpandCollapseBox'
import { ButtonInline } from 'Components/Buttons/ButtonInline'
import { Modal } from 'Components/Modal'
import { ModalHeader } from 'Components/Modal/ModalHeader'
import { ModalTitle } from 'Components/Modal/ModalTitle'
import { ModalBody } from 'Components/Modal/ModalBody'
import { ModalFooter } from 'Components/Modal/ModalFooter'
import SubmitButton from 'Components/Form/SubmitButton'
import { SnackBarContext } from 'Components/SnackBar'
import EditIcon from 'Assets/Images/icons/edit'
// import DownloadIcon from 'Assets/Images/icons/download'
import UploadIcon from 'Assets/Images/icons/upload'
import CancelIcon from 'Assets/Images/icons/cancel'
import { downloadStringAsFile } from 'Utils/Misc/index'
import { updateDMPrintingCosts } from '../../../../gql'
import {
  DMDocumentFormats,
  DMDocumentLabels,
} from '../../../../../../shared/constants'
import { apiClient } from '../../../../../../Module/API/Client.js'
import { authorization } from '../../../../../../Module/Auth/Auth.js'

import styles from './styles.js'

const useStyles = makeStyles(() => styles)

export const DMPrintingCosts = ({
  countryId,
  currencySign,
  DMPrintingCostsData,
  isEditingTable,
  visible,
}) => {
  const user = authorization.getUser()
  const classes = useStyles()
  const [headers, setHeaders] = useState([])
  const [printingCosts, setPrintingCosts] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [fileValidationError, setFileValidationError] = useState('')
  const [lastUpdaterUser, setLastUpdaterUser] = useState()

  const runSnackBar = useContext(SnackBarContext)

  const groupByQuantity = array =>
    array.reduce((objectsByKeyValue, obj) => {
      const value = obj.flyersCount
      objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj)
      return objectsByKeyValue
    }, {})

  const preparePrintingCostsData = nodes => {
    if (!nodes) return
    const sorterData = nodes.sort((a, b) => a.index - b.index)
    const headers = [
      ...new Set([
        'FLYERS_COUNT',
        ...sorterData.map(printingCost => printingCost.label),
      ]),
    ]

    const costsGroupedByQuantity = groupByQuantity(sorterData)
    const mappedCosts = Object.entries(costsGroupedByQuantity).reduce(
      (acc, [key, costs]) => {
        return [
          ...acc,
          {
            FLYERS_COUNT: key,
            ...costs.reduce(
              (accumulator, cost) => ({
                ...accumulator,
                [cost.label]: cost.value,
              }),
              {},
            ),
          },
        ]
      },
      [],
    )
    setHeaders(headers)
    setPrintingCosts(mappedCosts)
    setUploadedFile(null)
  }

  const savePrintingCosts = async data => {
    const response = await updateDMPrintingCosts({
      NewDMPrintingCosts: data,
    })
    const {
      data: {
        updateDMPrintingCosts: { DMPrintingCosts, error },
      },
    } = await response.json()

    if (DMPrintingCosts) {
      preparePrintingCostsData(DMPrintingCosts)
      runSnackBar({
        type: 'success',
        msg: `Printing Costs were saved successfully`,
      })
    } else {
      setUploadedFile(null)
      runSnackBar({
        type: 'error',
        msg: `There is an error while saving printing cost.`,
        error: error,
      })
    }
  }

  // const unparseFile = async printingCosts => {
  //   const papaParse = await import(
  //     /* webpackChunkName: "papaparse" */ 'papaparse'
  //   )
  //   const csv = papaParse.unparse(printingCosts)

  //   const blob = new Blob([csv], { type: 'text/csv' })
  //   const url = window.URL.createObjectURL(blob)
  //   const a = document.createElement('a')
  //   a.setAttribute('href', url)
  //   a.setAttribute('download', 'download.csv')
  //   a.click()
  // }

  const parseFile = async () => {
    setShowModal(false)
    const papaParse = await import(
      /* webpackChunkName: "papaparse" */ 'papaparse'
    )
    papaParse.parse(uploadedFile, {
      complete: results => {
        const nonEmptyColumns = results.data[0].filter(header => header)
        const dataForSaving = nonEmptyColumns
          .slice(1)
          .reduce((acc, column, index) => {
            const filteredPrintingCostsData = results.data
              .slice(1)
              .filter(data => data[0])
            return [
              ...acc,
              ...filteredPrintingCostsData.reduce(
                (accumulator, printingCostsData) => {
                  if (!printingCostsData.length || !printingCostsData[0])
                    return acc
                  return [
                    ...accumulator,
                    {
                      countryId: Number(countryId),
                      label: column,
                      value: Number(
                        Number(printingCostsData[index + 1]).toFixed(4),
                      ),
                      flyersCount: Number(printingCostsData[0]),
                      updatedBy: Number(user.id),
                      version: DMPrintingCostsData[0]
                        ? DMPrintingCostsData[0].version + 1
                        : 1,
                    },
                  ]
                },
                [],
              ),
            ]
          }, [])
        savePrintingCosts(dataForSaving)
      },
    })
  }

  const getLastUpdaterUser = async id => {
    const req = await apiClient.getUserById(id)
    const updater = await req.json()
    if (updater) {
      setLastUpdaterUser(updater)
    }
  }

  useEffect(() => {
    if (!lastUpdaterUser && DMPrintingCostsData && DMPrintingCostsData.length) {
      getLastUpdaterUser(DMPrintingCostsData[0].updatedBy)
    }
  }, [DMPrintingCostsData])

  useEffect(() => {
    preparePrintingCostsData(DMPrintingCostsData)
  }, [])

  const validateUploadedFile = async file => {
    if (file.type !== 'text/csv') {
      return setFileValidationError('Wrong file type. CSV required.')
    }

    papaParse.parse(file, {
      complete: result => {
        const { data } = result
        while (data.length) {
          const lastRow = data.pop()
          if (lastRow.findIndex(item => item) !== -1) {
            data.push(lastRow)
            break
          }
        }

        const headers = [...data[0]]
        const values = data.filter(
          (data, index) => index !== 0 && data.every(value => value),
        )

        if (values.length < data.length - 1) {
          return setFileValidationError(
            'Wrong file data. Empty values are not acceptable.',
          )
        }

        if (headers[0] !== 'FLYERS_COUNT') {
          return setFileValidationError(
            'There is an error in FLYERS_COUNT header.',
          )
        }

        const flyersCountObj = values.reduce((acc, value) => {
          return {
            ...acc,
            [value[0]]: acc[value[0]] ? acc[value[0]] + 1 : 1,
          }
        }, {})

        const isDuplicatesFlyersCount = !Object.values(flyersCountObj).every(
          count => count === 1,
        )

        if (isDuplicatesFlyersCount) {
          return setFileValidationError(
            `There are duplicates flyers count in the file.`,
          )
        }

        const isInvalidFlyerFormatValues = values.some(flyerFormatValues => {
          const isInvalidValues = flyerFormatValues.some((value, index) => {
            if (index === 0 && !/^[1-9]+\d+$/.test(value)) {
              setFileValidationError(
                `Wrong FLYERS_COUNT value ${value}. Only an integer value is allowed.`,
              )
              return true
            }
            if (index !== 0 && (isNaN(value) || Number(value) <= 0)) {
              setFileValidationError(
                `Wrong value ${value}. Printing cost can only include numbers and one dot.`,
              )
              return true
            }

            return false
          })

          if (isInvalidValues) {
            return true
          } else {
            return false
          }
        })

        if (isInvalidFlyerFormatValues) return
      },
    })
  }

  const handleFileUpload = async files => {
    validateUploadedFile(files[0])
    setUploadedFile(files[0])
  }

  const handleTemplateDownload = () => {
    const headers = [
      'FLYERS_COUNT',
      DMDocumentFormats.SMALL,
      DMDocumentFormats.BEST_SELLER,
      DMDocumentFormats.LARGE,
      DMDocumentFormats.JUMBO,
      DMDocumentFormats.OVERSIZED,
    ]
    const printingCostsTemplate =
      headers.join(',') +
      '\n' +
      headers.map(() => '').join(',') +
      '\n' +
      headers.map(() => '').join(',')
    downloadStringAsFile(
      printingCostsTemplate,
      'EDDMClientsPricing.csv',
      'text/csv',
    )
  }

  const renderHeaders = () => {
    return headers.map((header, index) => (
      <TableCell
        key={header}
        colSpan={1}
        align={index === 0 ? 'left' : 'center'}
        className={classes.borderRight}
      >
        <Typography>
          {index === 0 ? 'Quantity' : DMDocumentLabels[header]}
        </Typography>
      </TableCell>
    ))
  }

  const handleShowModal = () => {
    if (isEditingTable) {
      return
    }

    setShowModal(true)
  }

  return visible ? (
    <Box>
      <ExpandCollapseBox title={'EDDM Clients pricing'}>
        <Box display={'flex'} justifyContent={'flex-end'}>
          {/* {printingCosts.length ? (
            <ButtonInline
              className={classes.uploadBtn}
              label={'Download costs'}
              icon={<DownloadIcon style={{ marginLeft: '7px' }} />}
              labelFirst
              onClickHandler={() => unparseFile(printingCosts)}
            />
          ) : null} */}
          {printingCosts.length ? (
            <ButtonInline
              className={classes.uploadBtn}
              label={'Upload new costs'}
              icon={<EditIcon style={{ marginLeft: '7px' }} />}
              labelFirst
              onClickHandler={handleShowModal}
            />
          ) : null}
        </Box>
        {printingCosts.length ? (
          <Box display="flex" flexDirection="column" alignItems="end">
            <TableContainer className={classes.container}>
              <Table stickyHeader aria-label="sticky table" size="small">
                <TableHead className={classes.tableHead}>
                  <TableRow>{renderHeaders()}</TableRow>
                </TableHead>
                <TableBody>
                  {printingCosts.map(row => {
                    return (
                      <TableRow hover tabIndex={-1} key={row['FLYERS_COUNT']}>
                        {headers.map((column, index) => {
                          const value = row[column]
                          return (
                            <TableCell
                              key={column}
                              align={index === 0 ? 'left' : 'center'}
                              className={classes.borderRight}
                            >
                              <Typography>
                                {column === 'FLYERS_COUNT'
                                  ? Number(value).toLocaleString()
                                  : `${currencySign} ${Number(value).toFixed(
                                      4,
                                    )}`}
                              </Typography>
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box marginTop={1}>
              <Typography className={classes.dmCostsUpdatedData}>
                Table version
                {DMPrintingCostsData[0] ? DMPrintingCostsData[0].version : 0},
                Last updated{' '}
                {moment(printingCosts[0].updatedAt).format('MMM DD, YYYY')}
              </Typography>
              {lastUpdaterUser && (
                <Typography className={classes.dmCostsUpdatedData}>
                  by {lastUpdaterUser.firstName} {lastUpdaterUser.lastName}
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Box className={classes.noDataWrapper}>
            <Box className={classes.noData}>
              <Typography>No Data</Typography>
              <FileCopyTwoToneIcon color="#8A96A0" />
            </Box>
            <Link component="span" variant="body2" onClick={handleShowModal}>
              Upload new costs
            </Link>
          </Box>
        )}
      </ExpandCollapseBox>
      <Modal show={showModal} onEscapeKeyDown={() => setShowModal(false)}>
        <ModalHeader>
          <ModalTitle>Upload printing costs</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <Box>
            <Typography className={classes.mb16}>
              Upload a file in format .csv with the required column formatting.
              You can find a template here:{' '}
              <span
                className={classes.downloadTemplate}
                onClick={() => handleTemplateDownload()}
              >
                download template
              </span>
            </Typography>
            {/* <Typography className={classes.mb16}>
              {isUSOrCA
                ? '‘A6#250’, ‘DOOR_HANGER#250‘, and ‘TEARAWAY#250‘'
                : '‘A6#250‘ and ‘TEARAWAY#250‘'}{' '}
              formats must be present in the file. You can add multiple flyer
              weights, just make sure that each is in its own column (ex:
              A6#250, A6#300) .
            </Typography> */}
            <Typography className={cn(classes.underlined, classes.mb16)}>
              Existing data will be overwritten when a new file is uploaded.
            </Typography>
            <Typography className={classes.mb16}>
              On uploading, costs will automatically be rounded to 4 decimals.
              For example, $0.055289 will be rounded to $0.0553
            </Typography>
          </Box>

          {uploadedFile ? (
            <Box className={classes.uploaded}>
              <Typography>{uploadedFile.name}</Typography>
              <CancelIcon
                onClick={() => {
                  setUploadedFile(null)
                  setFileValidationError('')
                }}
              />
            </Box>
          ) : (
            <Box
              className={classes.uploadWrapper}
              onDrop={e => {
                e.preventDefault()
                e.stopPropagation()

                if (!uploadedFile) {
                  handleFileUpload(e.dataTransfer.files)
                }
              }}
              onDragOver={e => {
                e.preventDefault()
                e.stopPropagation()
              }}
            >
              <input
                style={{ display: 'none' }}
                id="upload-dm-printing-costs"
                type="file"
                onChange={e =>
                  !uploadedFile ? handleFileUpload(e.target.files) : {}
                }
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              />
              <label
                htmlFor="upload-dm-printing-costs"
                style={{
                  width: '100%',
                  height: '100%',
                  cursor: 'pointer',
                }}
              >
                <Box className={classes.upload}>
                  <UploadIcon />
                  <Typography>Upload</Typography>
                </Box>
              </label>
            </Box>
          )}
          {fileValidationError && (
            <Typography className={classes.fileValidationError}>
              Error! {fileValidationError}
            </Typography>
          )}
        </ModalBody>
        <ModalFooter>
          <SubmitButton
            value="Confirm"
            disabled={!uploadedFile || fileValidationError}
            onClick={() => parseFile()}
          />
          <SubmitButton
            value="Cancel"
            onClick={() => {
              setShowModal(false)
              setUploadedFile(null)
              setFileValidationError('')
            }}
          />
        </ModalFooter>
      </Modal>
    </Box>
  ) : null
}
